import React, { useState } from 'react'
import { Trash, Warning } from '@phosphor-icons/react'
import { formatDateTime } from '../utils'

const DeleteProject = ({
  project,
  delete_project_path,
  contents_count,
  project_path,
  projects_path,
}) => {
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState(null)
  // Check for active integrations
  const hasWebhook = project.webhook_url !== null
  const hasEmail = project.email_address !== null
  const hasApi = project.api_key !== null
  const hasIntegrations = hasWebhook || hasEmail || hasApi

  // Generate warning message for active integrations
  const getIntegrationWarnings = () => {
    const warnings = []
    if (hasWebhook) warnings.push('Webhook integration')
    if (hasEmail) warnings.push('Email integration')
    if (hasApi) warnings.push('API integration')

    if (warnings.length === 0) return null

    return warnings
  }

  const handleDelete = async () => {
    setIsDeleting(true)
    setError(null)
  
    try {
      const response = await fetch(delete_project_path, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector("meta[name='csrf-token']").content,
        },
      })
  
      if (response.ok) {
        Turbo.visit(projects_path)
      }
  
      try {
        const errorData = await response.json()
        setError(errorData.message || 'Failed to delete project')
      } catch {
        setError('Failed to delete project')
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      {/* Error Alert */}
      {error && (
        <div className="alert alert-error">
          <Warning className="h-4 w-4"/>
          <span>{error}</span>
        </div>
      )}

      {/* Main Content */}
      <div className="card bg-base-200">
        <div className="card-body">
          <h2 className="card-title text-error">
            <Trash className="h-5 w-5"/>
            Delete "{project.name}"
          </h2>

          <p className="py-4">
            Are you sure you want to delete this project? This action cannot be
            undone
            and will permanently remove all associated data.
          </p>

          {/* Integration Warnings */}
          {hasIntegrations && (
            <div className="alert alert-warning">
              <Warning className="h-5 w-5"/>
              <div>
                <h3 className="font-bold">Active Integrations Will Be
                  Deactivated</h3>
                <div className="text-sm">
                  The following integrations will be permanently disabled:
                  <ul className="list-disc list-inside mt-2">
                    {getIntegrationWarnings().map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Project Details */}
          <div className="bg-base-300 rounded-lg p-4 mt-4">
            <h3 className="font-medium mb-2">Project Details</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <span className="text-base-content/70">Created</span>
              <span>{formatDateTime(project.created_at)}</span>
              <span className="text-base-content/70">Last Updated</span>
              <span>{formatDateTime(project.updated_at)}</span>
              <span className="text-base-content/70">Project ID</span>
              <span>{project.id}</span>
              <span className="text-base-content/70">Contents</span>
              <span>{contents_count}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="card-actions justify-end mt-6">
            <a href={project_path} className="btn btn-ghost btn-xs">
              Cancel
            </a>
            <button
              className="btn btn-error btn-xs"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <span className="loading loading-spinner loading-xs"></span>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash className="h-3 w-3"/>
                  Delete Project
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DeleteProject